import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { sendPasswordResetEmail, signInWithEmailAndPassword } from "firebase/auth";
import { auth } from "../firebase";
import { useAuth } from "../context/AuthProvider";
import { venueDashboardPath } from "../lib/venueAccess";

function LoginPage() {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [notice, setNotice] = useState("");
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const {
        currentUser,
        userData,
        rawUserData,
        venueSelectionRequired,
        loading: authLoading,
        authError,
    } = useAuth();

    useEffect(() => {
        if (!currentUser || authLoading) return;
        if (rawUserData?.role === "ADMIN") {
            navigate("/admin", {replace: true});
        } else if (!rawUserData) {
            navigate("/signup", {replace: true});
        } else if (venueSelectionRequired || !userData?.businessID) {
            navigate("/select-venue", {replace: true});
        } else {
            navigate(venueDashboardPath(userData.role), {replace: true});
        }
    }, [authLoading, currentUser, navigate, rawUserData, userData, venueSelectionRequired]);

    const handleLogin = async () => {
        setLoading(true);
        setError("");
        setNotice("");
        try {
            await signInWithEmailAndPassword(auth, email, password);
        } catch (err) {
            setError("Login failed. Check your email or password.");
            console.error(err);
        }
        setLoading(false);
    };

    const handlePasswordReset = async () => {
        setError("");
        setNotice("");
        if (!email.trim()) {
            setError("Enter your email first, then request a password reset.");
            return;
        }
        setLoading(true);
        try {
            await sendPasswordResetEmail(auth, email.trim());
            setNotice("Password reset email sent. Check your inbox.");
        } catch (resetError) {
            setError("We could not send the reset email. Check the address and try again.");
            console.error(resetError);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-100 px-4">
            <div className="bg-white p-6 rounded shadow-md w-full max-w-sm">
                <h1 className="text-xl font-bold mb-4 text-center">Log In to Tiply</h1>

                <input
                    type="email"
                    placeholder="Email"
                    className="border p-2 w-full mb-3 rounded"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                />
                <input
                    type="password"
                    placeholder="Password"
                    className="border p-2 w-full mb-4 rounded"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                />

                {(error || authError) && <p className="text-sm text-red-600 mb-2">{error || authError}</p>}
                {notice && <p className="text-sm text-green-700 mb-2">{notice}</p>}

                <button
                    onClick={handleLogin}
                    disabled={loading}
                    className="w-full bg-blue-600 text-white py-2 rounded font-semibold"
                >
                    {loading ? "Logging in..." : "Login"}
                </button>
                <button type="button" onClick={handlePasswordReset} disabled={loading} className="mt-4 w-full text-sm font-medium text-blue-700 hover:underline">
                    Forgot password?
                </button>
                <p className="mt-5 text-center text-sm text-gray-600">
                    New to Tiply? <Link to="/signup" className="font-semibold text-blue-700 hover:underline">Create a business account</Link>
                </p>
            </div>
        </div>
    );
}

export default LoginPage;
